import { START_POINT_LETTER } from "helpers/bidpoint";
import shuffle from "lodash/shuffle";
import React from "react";
import { RolesModel } from "./roles";

export const imageExtensions = ["jpg", "jpeg", "png"];

export const videoExtensions = ["mp4", "webm", "ogg"];

export const audioExtensions = ["mp3", "mp4", "ogg", "wav"];

export function isDesktop() {
    return window.innerWidth > 1024;
}

export function isObject(value: any) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

export const hasValue = (value: unknown) => value !== "" && value !== null && value !== undefined;

const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export const pointLetter = (index: number, firstLoadPointIndex = 0): string => {
    if (index < 0) {
        return "";
    }
    if (index < firstLoadPointIndex) {
        return `${START_POINT_LETTER}${pointLetter(index)}`;
    }
    const pointIndex = index - firstLoadPointIndex;
    const trunc = Math.trunc(pointIndex / alphabet.length);
    const letterIndex = pointIndex % alphabet.length;
    return trunc ? `${alphabet[trunc - 1]}${alphabet[letterIndex]}` : alphabet[letterIndex];
};

const passwordCharset = { letters: "abcdefghjkmnpqrstuvwxyz", numbers: "123456789" };

export const generatePassword = () => {
    let password = "";
    for (let i = 0; i < 6; ++i) {
        password += passwordCharset.letters.charAt(Math.floor(Math.random() * passwordCharset.letters.length));
    }
    for (let i = 0; i < 2; ++i) {
        password += passwordCharset.numbers.charAt(Math.floor(Math.random() * passwordCharset.numbers.length));
    }
    return shuffle(password).join("");
};

export function arraysEqual(a: (number | undefined)[], b: (number | undefined)[]) {
    if (a === b) {
        return true;
    }
    if (a == null || b == null) {
        return false;
    }
    if (a.length !== b.length) {
        return false;
    }

    // If you don't care about the order of the elements inside
    // the array, you should sort both arrays here.
    // Please note that calling sort on an array will modify that array.
    // you might want to clone your array first.

    for (let i = 0; i < a.length; ++i) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
}

export const formatNumber = (
    number: Number,
    options: Intl.NumberFormatOptions | undefined = { maximumFractionDigits: 2 },
) => {
    if (!number && number !== 0) {
        return "";
    }
    if (options.style === "currency" && !options.currency) {
        options.currency = "RUB";
    }
    return number.toLocaleString("ru-RU", options);
};

export const truncateString = (str: string, num: number, omission = "...") => {
    if (str.length <= num) {
        return str;
    }
    return str.slice(0, num) + omission;
};

export const alphabeticalSort = (a: { [key: string]: string }, b: { [key: string]: string }, prop: string) => {
    if (!a[prop]) {
        return 1;
    }
    return a[prop].localeCompare(b[prop], "ru", { sensitivity: "base" });
};

export const enumLabel = (
    dictionary: Array<{ label: string; value?: string | undefined }>,
    value: string,
    defaultValue = "",
) => {
    const enumObj = (dictionary || []).find((item) => item.value === value);
    return enumObj ? enumObj.label : defaultValue || value;
};

export const flagVal = (
    dictionary: Array<{ label: string; value?: string | undefined; intValue: number }>,
    value: string,
) => {
    const enumObj = dictionary.find((item) => item.value === value);
    return enumObj ? enumObj.intValue : 0;
};

export const allFlagValues = (dictionary: Array<{ label: string; value: number; intValue: number }>, value: number) => {
    const selected = (intValue: number) => (value & intValue) === intValue;
    return dictionary.map((item) => (
        <div key={item.intValue}>
            <span className="text-muted">{item.label}</span>: {selected(item.intValue) ? "Да" : "Нет"}
        </div>
    ));
};

export const formattedCount = (count: number) => (count < 100 ? count : "99+");

export const optionName = (name: string) => name.charAt(0)?.toLowerCase() + name.slice(1);

export const paramsObject = (queryString: string) => {
    const result: { [key: string]: string } = {};
    for (const [key, value] of new URLSearchParams(queryString).entries()) {
        result[key] = value;
    }
    return result;
};

export const ymEvent = (eventName: string, params?: Record<string, unknown>) => {
    const ymId = process.env.YM_ID;

    if (
        process.env.APP_ENV !== "production" ||
        typeof window === "undefined" ||
        typeof window.ym !== "function" ||
        !ymId
    ) {
        return;
    }

    window.ym(Number(ymId), "reachGoal", eventName, params);
};

export function formatPhoneNumber(phone: string) {
    if (!phone) {
        return null;
    }

    const phoneRegex = /\+\d{11}/;
    if (phoneRegex.test(phone || "")) {
        return `+${phone[1]} (${phone.substring(2, 5)}) ${phone.substring(5, 8)}-${phone.substring(8, 10)}-${phone.substring(10)}`;
    } else {
        return phone;
    }
}

export function readFile(messageFile: Blob) {
    return new Promise(function (resolve, reject) {
        const fileReader = new FileReader();

        fileReader.onload = function () {
            resolve(this.result);
        };

        if (messageFile.size > 25_000_000) {
            reject(new Error("Файл для подписи не должен превышать " + 25_000_000 / 1_000_000 + "МБ"));
            return;
        }

        fileReader.readAsArrayBuffer(messageFile);
    });
}

export function downloadURI(uri: string, name: string) {
    const link = document.createElement("a");
    link.download = name;
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export function downloadTextAsFile(filename: string, text: string) {
    const element = document.createElement("a");
    element.setAttribute("href", "data:text/plain;charset=utf-8," + encodeURIComponent(text));
    element.setAttribute("download", filename);

    element.style.display = "none";
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
}

export function canManageOrganization(roles: RolesModel) {
    const { isRoot, isTechSupport, isIntegrationManager, isAccountManager } = roles;
    return (
        isRoot ||
        isTechSupport ||
        isIntegrationManager ||
        isAccountManager ||
        localStorage.getItem("canManageOrganization") === "true"
    );
}

export const jsxJoin = (arr: React.ReactNode[], separator: React.ReactNode): React.ReactNode => {
    if (arr.length === 0) {
        return null;
    }

    return (
        <>
            {arr.map((item, index) => (
                <React.Fragment key={index}>
                    {item}
                    {index < arr.length - 1 && separator}
                </React.Fragment>
            ))}
        </>
    );
};

export function formatPhone(phone: string) {
    const digits = phone.replace(/^8/, "7").replace(/[^\d]+/, "");
    if (digits.length < 11) {
        return phone;
    }

    // Домашний?
    if (digits.substring(0, 2) === "74") {
        // Для белгородских (и похожих) домашних возвращаем нормальное форматирование
        // Для остальных пытаемся
        return digits.substring(0, 3) === "747"
            ? digits.replace(/^(\d)(\d+)(\d\d)(\d\d)(\d\d)$/, "+$1 ($2) $3-$4-$5")
            : digits.replace(/^(\d)(\d+)(\d\d\d)(\d\d)(\d\d)$/, "+$1 ($2) $3-$4-$5");
    }

    // Для русских мобильных возвращаем нормальное форматирование
    // Для остальных пытаемся
    return digits.substring(0, 2) === "79"
        ? digits.replace(/^(\d)(\d+)(\d\d\d)(\d\d)(\d\d)$/, "+$1 $2 $3-$4-$5")
        : digits.replace(/^(\d)(\d+)(\d\d\d)(\d\d)(\d\d)$/, "+$1$2 $3-$4-$5");
}

export function lowerFirst(str: string) {
    return str.charAt(0).toLowerCase() + str.slice(1);
}
